import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useQuery } from "@tanstack/react-query";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { commonFilterContains, CreateHeader, Filter } from "../filter/base-filter";
import { EmailUserDto, MailTemplateDto } from "../../services/account/account.dto";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import { useToast } from "../../utils/toast";
import AdminService from "../../services/admin/admin.services";

type MailTemplate = MailTemplateDto | { id: string; name: string; category: string; subject: string; message: string; builtIn?: boolean };
const defaultTemplates: MailTemplate[] = [
  {
    id: "newsletter",
    name: "Yeni bülten duyurusu",
    category: "Bülten",
    subject: "Yeni bültenimiz yayında",
    message: `<div style="font-family:Arial,sans-serif;color:#24324a;max-width:680px;margin:auto"><div style="background:#123b68;padding:28px;text-align:center;color:white"><div style="font-size:34px">📰</div><h1 style="margin:10px 0 0">Yeni Bültenimiz Yayında</h1></div><div style="padding:28px"><p>Merhaba,</p><p>Güncel gelişmeleri ve önemli duyuruları derlediğimiz yeni bültenimizi sizinle paylaşmaktan mutluluk duyuyoruz.</p><p>Keyifli okumalar dileriz.</p><p style="margin-top:28px"><a href="#" style="background:#e57c26;color:#fff;padding:13px 22px;border-radius:6px;text-decoration:none;font-weight:bold">Bülteni Görüntüle</a></p></div></div>`
  },
  {
    id: "event",
    name: "Etkinlik daveti",
    category: "Etkinlik",
    subject: "Etkinliğimize davetlisiniz",
    message: `<div style="font-family:Arial,sans-serif;max-width:680px;margin:auto;color:#24324a"><div style="padding:24px;background:#eef5fb;text-align:center"><div style="font-size:36px">📅</div><h1>Etkinliğimize Davetlisiniz</h1></div><div style="padding:28px"><p>Merhaba,</p><p>Sizi yaklaşan etkinliğimize bekliyoruz. Program ve katılım detayları için aşağıdaki bağlantıyı kullanabilirsiniz.</p><p style="text-align:center"><a href="#" style="background:#123b68;color:white;padding:13px 22px;border-radius:6px;text-decoration:none;font-weight:bold">Detayları İncele</a></p></div></div>`
  },
  {
    id: "announcement",
    name: "Genel bilgilendirme",
    category: "Duyuru",
    subject: "Önemli bilgilendirme",
    message: `<div style="font-family:Arial,sans-serif;max-width:680px;margin:auto;color:#24324a"><div style="border-top:5px solid #e57c26;padding:28px"><div style="font-size:34px">📣</div><h2>Önemli Bilgilendirme</h2><p>Merhaba,</p><p>Bu alanı kurumunuzun duyurusuna göre düzenleyebilirsiniz.</p><p>İlginiz için teşekkür ederiz.</p></div></div>`
  }
];

const MassEmail = () => {
  const toast = useToast();
  const dt = useRef<DataTable<EmailUserDto[]>>(null);
  const [filters, setFilters] = useState<DataTableFilterMeta>({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [selectedUserListesi, setSelectedUserListesi] = useState<EmailUserDto[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState<MailTemplate[]>(defaultTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState<string | number | null>(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [showEmojiDialog, setShowEmojiDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const editorRef = useRef<any>(null);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { data: UsersMailResponse, isLoading: usersResponseLoading } = useQuery({
    queryKey: ["UsersMail"],
    refetchOnMount: false,
    queryFn: async () => await AdminService.getUserForMassEmail()
  });

  const { data: MailTemplatesResponse, refetch: refetchMailTemplates } = useQuery({
    queryKey: ["MailTemplates"],
    queryFn: async () => await AdminService.getMailTemplates()
  });

  useEffect(() => {
    const savedTemplates = (MailTemplatesResponse?.result?.data || []) as MailTemplateDto[];
    setTemplates([...defaultTemplates, ...savedTemplates]);
  }, [MailTemplatesResponse]);

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: commonFilterContains,
      full_name: commonFilterContains,
      osb: commonFilterContains,
      osb_gorevi: commonFilterContains,
    });
    setGlobalFilterValue("");
  };

  useEffect(() => {
    initFilters();
  }, []);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const next = { ...filters };
    // @ts-ignore
    next.global.value = value;
    setFilters(next);
    setGlobalFilterValue(value);
  };

  const applyTemplate = (id: string | number | null) => {
    if (!id) return;
    const template = templates.find((item) => item.id === id);
    if (!template) return;
    setSelectedTemplate(id);
    setSubject(template.subject);
    setMessage(template.message);
    setError("");
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !subject.trim() || !message.trim()) {
      setError("Taslak adı, konu ve içerik zorunludur.");
      return;
    }
    try {
      const response = await AdminService.createMailTemplate({
        name: templateName.trim(),
        category: "Benim taslaklarım",
        subject,
        message
      });
      const created = response?.result?.data as MailTemplateDto;
      await refetchMailTemplates();
      setSelectedTemplate(created?.id ?? null);
      setTemplateName("");
      setShowTemplateDialog(false);
      toast.show("Taslak veritabanına kaydedildi", "success");
    } catch {
      setError("Taslak kaydedilemedi.");
      toast.show("Taslak kaydedilemedi", "error");
    }
  };

  const plainMessage = message.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
  const wordCount = plainMessage ? plainMessage.split(/\s+/).length : 0;
  const imageCount = (message.match(/<img\b/gi) || []).length;
  const estimatedReadTime = Math.max(1, Math.ceil(wordCount / 180));

  const toggleHtmlEditor = () => {
    setShowHtmlSource((value) => !value);
  };

  const undo = () => editorRef.current?.getQuill?.()?.history.undo();
  const redo = () => editorRef.current?.getQuill?.()?.history.redo();
  const insertEmoji = (emoji: string) => {
    const quill = editorRef.current?.getQuill?.();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range?.index ?? quill.getLength(), emoji, "user");
      quill.setSelection((range?.index ?? quill.getLength()) + emoji.length, 0, "user");
    } else {
      setMessage((current) => `${current}${emoji}`);
    }
    setShowEmojiDialog(false);
  };

  const addAttachments = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const nextFiles = [...attachments, ...files];
    const totalSize = nextFiles.reduce((total, file) => total + file.size, 0);
    if (nextFiles.length > 5) {
      setError("En fazla 5 dosya ekleyebilirsiniz.");
      return;
    }
    if (totalSize > 20 * 1024 * 1024) {
      setError("Eklerin toplam boyutu 20 MB'ı geçemez.");
      return;
    }
    setAttachments(nextFiles);
    event.target.value = "";
  };

  const removeAttachment = (index: number) => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));

  const editorHeader = (
    <span className="ql-formats">
      <select className="ql-font" defaultValue="">
        <option value="">Yazı tipi</option>
        <option value="serif">Serif</option>
        <option value="sans-serif">Sans Serif</option>
        <option value="monospace">Monospace</option>
      </select>
      <select className="ql-size" defaultValue="">
        <option value="">Boyut</option>
        <option value="small">Küçük</option>
        <option value="large">Büyük</option>
        <option value="huge">Çok büyük</option>
      </select>
      <select className="ql-header" defaultValue="">
        <option value="">Normal</option>
        <option value="1">Başlık 1</option>
        <option value="2">Başlık 2</option>
        <option value="3">Başlık 3</option>
      </select>
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-strike" />
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
      <button className="ql-link" />
      <button className="ql-image" />
      <button className="ql-blockquote" />
      <button className="ql-align" />
      <select className="ql-color" />
      <select className="ql-background" />
      <button className="ql-clean" />
    </span>
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserListesi.length) {
      setError("Lütfen en az bir kullanıcı seçiniz.");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setError("Konu ve içerik zorunludur.");
      return;
    }
    setError("");
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setShowConfirmDialog(false);
    setSuccess("");
    try {
      const request = new FormData();
      request.append("subject", subject);
      request.append("message", message);
      selectedUserListesi.forEach((user) => request.append("selected_users", String(user.id)));
      attachments.forEach((file) => request.append("attachments", file, file.name));
      const response = await AdminService.sendMassEmail(request);
      if (response?.result) {
        setSuccess(`${response.result.data}`);
        setSubject("");
        setMessage("");
        setAttachments([]);
        setSelectedTemplate(null);
        toast.show(`${response.result.data}`, "success");
      } else {
        setError(`${response.data.message}`);
      }
    } catch {
      setError("Mail gönderilirken bir hata oluştu, lütfen tekrar deneyiniz.");
      toast.show("Mail gönderilemedi", "error");
    } finally {
      setLoading(false);
    }
  };

  const userData = (UsersMailResponse?.result?.data || []) as EmailUserDto[];
  const templateOptions = templates.map((item) => ({
    label: `${item.name} · ${item.category}`,
    value: item.id
  }));

  return (
    <div className="flex flex-column gap-3">
      <div className="card p-0 overflow-hidden">
        <div className="p-4" style={{ background: "linear-gradient(135deg,#123b68,#1d5b91)", color: "white" }}>
          <div className="flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div className="text-2xl font-bold">Toplu E-posta Gönderimi</div>
              <div className="mt-2 opacity-90">Alıcıları seçin, mesajınızı hazırlayın ve kampanyanızı gönderin.</div>
            </div>
            <div className="flex gap-2">
              <Button
                label="Önizleme"
                icon="pi pi-eye"
                onClick={() => setShowPreviewDialog(true)}
                className="p-button-sm"
                disabled={!message}
              />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 flex flex-column gap-3">
          {success && <Message severity="success" text={success} className="w-full" />}
          {error && <Message severity="error" text={error} className="w-full" />}

          <section className="border-1 surface-border border-round p-3">
            <div className="mb-3">
              <h3 className="m-0 text-lg">1. E-posta içeriğini hazırlayın</h3>
              <small className="text-600">Yeni bir mesaj yazın veya kayıtlı bir taslağı yükleyip düzenleyin.</small>
            </div>
            <div className="flex flex-column gap-3">
              <div className="border-1 border-round p-3" style={{ background: "#f5f8fc", borderColor: "#d7e3ef" }}>
                <div className="flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="flex align-items-start gap-3">
                    <span className="flex align-items-center justify-content-center border-circle surface-card text-primary" style={{ width: 40, height: 40 }}><i className="pi pi-folder-open" /></span>
                    <div>
                      <div className="font-semibold">Kayıtlı taslaktan başla</div>
                      <small className="text-600">Taslak seçtiğinizde konu ve içerik otomatik doldurulur.</small>
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <Dropdown
                      value={selectedTemplate}
                      options={templateOptions}
                      onChange={(e) => applyTemplate(e.value)}
                      placeholder="Taslak seçin (isteğe bağlı)"
                      className="w-full md:w-20rem"
                      showClear
                    />
                    <Button type="button" label="Taslağı kaydet" icon="pi pi-bookmark" outlined size="small" onClick={() => setShowTemplateDialog(true)} disabled={!subject.trim() || !message.trim()} />
                  </div>
                </div>
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="subject" className="font-bold">Konu</label>
                <InputText
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Örn. Yeni bültenimiz yayında"
                  required
                  className="w-full"
                />
              </div>
              <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <label className="font-bold block">Mesaj içeriği</label>
                  <small className="text-600">Metni biçimlendirin, görsel veya dosya ekleyin.</small>
                </div>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" icon="pi pi-undo" tooltip="Geri al" size="small" text onClick={undo} />
                <Button type="button" icon="pi pi-refresh" tooltip="İleri al" size="small" text onClick={redo} />
                <Button type="button" icon="pi pi-smile" tooltip="Emoji ekle" size="small" text onClick={() => setShowEmojiDialog(true)} />
                <label className="p-button p-button-text p-button-sm m-0" title="Dosya ekle">
                  <i className="pi pi-paperclip" />
                  <input type="file" multiple hidden onChange={addAttachments} />
                </label>
                <Button type="button" icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"} tooltip={isFullscreen ? "Tam ekrandan çık" : "Tam ekran"} size="small" text onClick={() => setIsFullscreen((value) => !value)} />
                <Button type="button" label={showHtmlSource ? "Editöre dön" : "HTML"} icon="pi pi-code" size="small" outlined onClick={toggleHtmlEditor} />
              </div>
            </div>
            {showHtmlSource ? (
              <InputTextarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                autoResize
                rows={14}
                className="w-full font-mono"
                placeholder="E-posta HTML içeriği"
              />
            ) : (
              <div
                className="border-1 surface-border border-round overflow-hidden"
                style={isFullscreen ? { position: "fixed", inset: 0, zIndex: 1100, background: "var(--surface-card)", padding: "1rem" } : undefined}
              >
                {isFullscreen && <div className="flex justify-content-between align-items-center mb-2"><b>E-posta içeriği</b><Button icon="pi pi-times" text onClick={() => setIsFullscreen(false)} /></div>}
                <Editor
                  ref={editorRef}
                  value={message}
                  onTextChange={(e: EditorTextChangeEvent) => setMessage(e.htmlValue ?? "")}
                  headerTemplate={editorHeader}
                  style={{ height: isFullscreen ? "calc(100vh - 5rem)" : "360px" }}
                />
              </div>
            )}
            {attachments.length > 0 && (
              <div className="flex flex-column gap-2 border-1 surface-border border-round p-3 surface-50">
                <div className="flex justify-content-between align-items-center"><b><i className="pi pi-paperclip mr-2" />Ekler ({attachments.length})</b><small className="text-600">Toplam {(attachments.reduce((total, file) => total + file.size, 0) / 1024 / 1024).toFixed(2)} MB</small></div>
                {attachments.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex align-items-center justify-content-between surface-card border-round p-2">
                    <span className="flex align-items-center gap-2 text-sm"><i className="pi pi-file" />{file.name}<small className="text-500">({(file.size / 1024).toFixed(0)} KB)</small></span>
                    <Button type="button" icon="pi pi-times" text rounded severity="danger" size="small" onClick={() => removeAttachment(index)} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-content-between flex-wrap gap-2 surface-50 border-1 surface-border border-round p-2 text-sm text-600">
              <span><i className="pi pi-align-left mr-1" />{wordCount} kelime</span>
              <span><i className="pi pi-clock mr-1" />Tahmini okuma: {estimatedReadTime} dk</span>
              <span><i className="pi pi-image mr-1" />{imageCount} görsel</span>
              <span><i className="pi pi-mobile mr-1" />Mobil uyumlu içerik</span>
            </div>
            </div>
          </section>

          <section className="border-1 surface-border border-round p-3">
            <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h3 className="m-0 text-lg">2. Alıcıları seçin</h3>
                <small className="text-600">İçeriğiniz hazır olduğunda gönderimin yapılacağı kullanıcıları belirleyin.</small>
              </div>
              <span className="p-tag p-tag-info"><i className="pi pi-users mr-2" />{selectedUserListesi.length} alıcı seçildi</span>
            </div>
            <DataTable
              value={userData}
              ref={dt}
              dataKey="id"
              showGridlines
              rows={8}
              rowsPerPageOptions={[8, 20, 50]}
              emptyMessage="Kullanıcı bulunamadı."
              loading={usersResponseLoading}
              paginator
              filters={filters}
              globalFilterFields={["email", "full_name", "osb", "osb_gorevi"]}
              header={CreateHeader("Kullanıcılar", globalFilterValue, onGlobalFilterChange, initFilters)}
              selectionMode="multiple"
              selection={selectedUserListesi}
              onSelectionChange={(e) => setSelectedUserListesi(e.value)}
            >
              <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
              <Column field="email" header="E-posta" sortable filter filterPlaceholder="E-posta ile ara" filterClear={Filter.FilterClearTemplate} filterApply={Filter.FilterApplyTemplate} />
              <Column field="full_name" header="Ad Soyad" sortable filter filterPlaceholder="Ad Soyad ile ara" filterClear={Filter.FilterClearTemplate} filterApply={Filter.FilterApplyTemplate} />
              <Column field="osb" header="Kurum" sortable />
              <Column field="osb_gorevi" header="Görev" sortable />
            </DataTable>
          </section>

          <div className="flex justify-content-between align-items-center flex-wrap gap-2 border-top-1 surface-border pt-3">
            <small className="text-600">
              <i className="pi pi-info-circle mr-1" />
              E-posta istemcileri için görsellerde 680 px genişlik ve 2 MB altı dosya boyutu önerilir.
            </small>
            <Button
              type="submit"
              label={loading ? "Gönderiliyor..." : `Alıcıları gözden geçir ve gönder${selectedUserListesi.length ? ` (${selectedUserListesi.length})` : ""}`}
              icon="pi pi-send"
              loading={loading}
              disabled={loading || !subject || !message || !selectedUserListesi.length}
            />
          </div>
        </form>
      </div>

      <Dialog
        visible={showTemplateDialog}
        onHide={() => setShowTemplateDialog(false)}
        header="Yeni taslak kaydet"
        modal
        style={{ width: "420px" }}
      >
        <div className="flex flex-column gap-3">
          <small className="text-600">Mevcut konu ve içerik yeni bir taslak olarak kaydedilecek.</small>
          <InputText
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Taslak adı"
            autoFocus
          />
          <Button label="Taslağı Kaydet" icon="pi pi-check" onClick={saveTemplate} />
        </div>
      </Dialog>

      <Dialog
        visible={showEmojiDialog}
        onHide={() => setShowEmojiDialog(false)}
        header="Emoji ekle"
        modal
        style={{ width: "360px" }}
      >
        <div className="flex flex-wrap gap-2">
          {["😀", "🙂", "😉", "😍", "🎉", "👏", "👍", "✅", "⭐", "📣", "📌", "📅", "💡", "🔥", "❤️", "🙏", "🚀", "🌱", "🎁", "✨"].map((emoji) => (
            <Button key={emoji} type="button" label={emoji} text className="text-2xl" onClick={() => insertEmoji(emoji)} />
          ))}
        </div>
      </Dialog>

      <Dialog
        visible={showPreviewDialog}
        onHide={() => setShowPreviewDialog(false)}
        header="Mail önizleme"
        modal
        style={{ width: "850px", maxWidth: "95vw" }}
      >
        <div className="flex justify-content-end mb-3 gap-2">
          <Button
            icon="pi pi-desktop"
            label="Masaüstü"
            outlined={previewMode !== "desktop"}
            onClick={() => setPreviewMode("desktop")}
          />
          <Button
            icon="pi pi-mobile"
            label="Mobil"
            outlined={previewMode !== "mobile"}
            onClick={() => setPreviewMode("mobile")}
          />
        </div>
        <div className="border-1 surface-border border-round p-3 surface-50 flex justify-content-center">
          <div
            style={{
              width: previewMode === "mobile" ? "375px" : "100%",
              minHeight: "420px",
              background: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0,0,0,.08)"
            }}
          >
            <div className="text-900 font-bold border-bottom-1 surface-border pb-3 mb-3">
              {subject || "E-posta konusu"}
            </div>
            <div dangerouslySetInnerHTML={{ __html: message || "İçerik burada görünecek." }} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        header="Gönderim onayı"
        modal
        style={{ width: "520px" }}
        footer={
          <div>
            <Button label="İptal" text onClick={() => setShowConfirmDialog(false)} />
            <Button label="Gönder" icon="pi pi-send" onClick={handleConfirmSend} />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <Message severity="warn" text={`${selectedUserListesi.length} kişiye mail gönderilecek. Bu işlem geri alınamaz.`} />
          <div><b>Konu:</b> {subject}</div>
          <div
            className="surface-100 p-3 border-round"
            style={{ maxHeight: "160px", overflow: "auto" }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default MassEmail;
